import React from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import { themeGet } from "@styled-system/theme-get";
import Box from "components/global/Box";
import Text from "components/global/Text";
import { space, width } from "styled-system";
import media from "utils/media";
import logoSmall from "assets/logo_small.svg";

// Generate unique ID for accessibility
let idCounter = 0;
const generateId = () => `spectrum-${++idCounter}`;

const Container = styled(Box)`
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
  ${space}
  ${width}
`;

const LabelsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${(p) => p.theme.space[300]};
`;

const Label = styled(Text)`
  ${(p) => p.theme.typography[200]}
  font-weight: ${(p) => p.theme.fontWeights.bold};
  color: ${(p) => p.theme.colors.neutral[700]};
  text-transform: uppercase;
  letter-spacing: 0.05em;

  ${media.small`
    font-size: 12px;
  `}

  ${media.tiny`
    font-size: 10px;
  `}
`;

const LineContainer = styled.div`
  position: relative;
  width: 100%;
  height: 4px;
  background: ${(p) =>
    p.lineColor
      ? themeGet(`colors.${p.lineColor}`)(p)
      : p.theme.colors.neutral[800]};
  border-radius: 2px;
  margin: ${(p) => p.theme.space[400]} 0;

  ${media.small`
    height: 3px;
  `}
`;

const IconWrapper = styled.div`
  position: absolute;
  top: calc(50% + 16px);
  left: ${(p) => p.percentage}%;
  transform: translate(-50%, -50%);
  width: ${(p) => p.iconSize}px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg,
  img {
    width: 100%;
    height: auto;
    display: block;
    vertical-align: middle;
  }

  ${media.small`
    width: ${(p) => p.iconSize * 0.75}px;
  `}

  ${media.tiny`
    width: ${(p) => p.iconSize * 0.6}px;
  `}
`;

const Spectrum = ({
  percentage,
  leftLabel,
  rightLabel,
  icon,
  lineColor,
  iconSize,
  description,
  ...rest
}) => {
  // Generate unique ID for accessibility
  const [id] = React.useState(generateId);

  // Clamp percentage between 0-100
  const clampedPercentage = Math.min(Math.max(percentage, 0), 100);

  return (
    <Container
      role="group"
      aria-label={description || `${leftLabel} to ${rightLabel} spectrum`}
      {...rest}
    >
      <LabelsContainer>
        <Label id={`${id}-left`} as="span">
          {leftLabel}
        </Label>
        <Label id={`${id}-right`} as="span">
          {rightLabel}
        </Label>
      </LabelsContainer>

      <LineContainer
        role="img"
        aria-labelledby={`${id}-left ${id}-right`}
        lineColor={lineColor}
      >
        <IconWrapper
          percentage={clampedPercentage}
          iconSize={iconSize}
          aria-hidden="true"
        >
          {icon || <img src={logoSmall} alt="" />}
        </IconWrapper>
      </LineContainer>
    </Container>
  );
};

Spectrum.propTypes = {
  percentage: PropTypes.number.isRequired,
  leftLabel: PropTypes.string.isRequired,
  rightLabel: PropTypes.string.isRequired,
  icon: PropTypes.node,
  lineColor: PropTypes.string,
  iconSize: PropTypes.number,
  description: PropTypes.string,
};

Spectrum.defaultProps = {
  iconSize: 32,
  lineColor: null,
  icon: null,
  description: null,
};

export default Spectrum;
